import { useState } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import { 
  Search, 
  Filter, 
  Plus, 
  MoreHorizontal, 
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Users,
  Layers,
  ArrowUpRight
} from "lucide-react";

export default function ProjectsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  
  // Mock project data
  const projects = [
    {
      id: 1,
      name: "Website Redesign",
      description: "Redesign of the company website with new branding",
      status: "in-progress",
      progress: 65,
      dueDate: "Jun 15, 2025",
      team: [
        { id: 1, name: "Olivia M.", avatar: "OM" },
        { id: 2, name: "Jackson L.", avatar: "JL" },
        { id: 3, name: "Isabella N.", avatar: "IN" }
      ],
      tasks: {
        total: 24,
        completed: 16
      }
    },
    {
      id: 2,
      name: "Mobile App Development",
      description: "Development of the new mobile application",
      status: "in-progress",
      progress: 42,
      dueDate: "Aug 30, 2025",
      team: [
        { id: 2, name: "Jackson L.", avatar: "JL" },
        { id: 4, name: "William K.", avatar: "WK" },
        { id: 5, name: "Sofia D.", avatar: "SD" },
        { id: 6, name: "Ethan J.", avatar: "EJ" }
      ],
      tasks: {
        total: 36,
        completed: 15
      }
    },
    {
      id: 3,
      name: "Marketing Campaign",
      description: "Q3 marketing campaign for product launch",
      status: "planning",
      progress: 15,
      dueDate: "Jul 10, 2025",
      team: [
        { id: 1, name: "Olivia M.", avatar: "OM" },
        { id: 5, name: "Sofia D.", avatar: "SD" },
        { id: 7, name: "Ava W.", avatar: "AW" }
      ],
      tasks: {
        total: 18,
        completed: 3
      }
    },
    {
      id: 4,
      name: "Product Analytics Dashboard",
      description: "Internal analytics dashboard for product metrics",
      status: "completed",
      progress: 100,
      dueDate: "May 5, 2025",
      team: [
        { id: 3, name: "Isabella N.", avatar: "IN" },
        { id: 8, name: "Noah B.", avatar: "NB" }
      ],
      tasks: {
        total: 12,
        completed: 12
      }
    },
    {
      id: 5,
      name: "Customer Feedback System",
      description: "Implementation of new customer feedback collection system",
      status: "in-progress",
      progress: 78,
      dueDate: "Jun 22, 2025",
      team: [
        { id: 4, name: "William K.", avatar: "WK" },
        { id: 9, name: "Emma J.", avatar: "EJ" }
      ],
      tasks: {
        total: 15,
        completed: 12
      }
    },
    {
      id: 6,
      name: "API Integration",
      description: "Integration with third-party payment providers",
      status: "planning",
      progress: 10,
      dueDate: "Jul 30, 2025",
      team: [
        { id: 2, name: "Jackson L.", avatar: "JL" },
        { id: 10, name: "Liam G.", avatar: "LG" }
      ],
      tasks: {
        total: 20,
        completed: 2
      }
    },
    {
      id: 7,
      name: "Security Audit",
      description: "Comprehensive security audit of all systems",
      status: "on-hold",
      progress: 35,
      dueDate: "Aug 15, 2025",
      team: [
        { id: 8, name: "Noah B.", avatar: "NB" },
        { id: 10, name: "Liam G.", avatar: "LG" },
        { id: 3, name: "Isabella N.", avatar: "IN" }
      ],
      tasks: {
        total: 22,
        completed: 8
      }
    },
    {
      id: 8,
      name: "Content Management System",
      description: "Development of new CMS for marketing team",
      status: "completed",
      progress: 100,
      dueDate: "Apr 30, 2025",
      team: [
        { id: 1, name: "Olivia M.", avatar: "OM" },
        { id: 4, name: "William K.", avatar: "WK" },
        { id: 6, name: "Ethan J.", avatar: "EJ" }
      ],
      tasks: {
        total: 30,
        completed: 30
      }
    }
  ];
  
  // Filter projects based on search query and status
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          project.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === "all" || project.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });
  
  // Count projects by status
  const statusCounts = {
    total: projects.length,
    inProgress: projects.filter(p => p.status === "in-progress").length,
    planning: projects.filter(p => p.status === "planning").length,
    completed: projects.filter(p => p.status === "completed").length,
    onHold: projects.filter(p => p.status === "on-hold").length
  };
  
  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "in-progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "planning":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "on-hold":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };
  
  return (
    <DashboardLayout>
      <PageHeader 
        title="Projects" 
        description="Manage and track all your projects"
      />
      
      {/* Stats Cards */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Projects</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <Layers className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{statusCounts.total}</span>
            <span className="ml-2 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
              <ArrowUpRight className="h-3 w-3 mr-1" />
              +2
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Added this month</div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">In Progress</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{statusCounts.inProgress}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Active projects</div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <CheckCircle2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{statusCounts.completed}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Finished projects</div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">On Hold</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <AlertCircle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{statusCounts.onHold}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Paused projects</div>
        </div>
      </div>
      
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative w-full sm:w-64 md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search projects..."
            className="pl-10 pr-4 py-2 w-full rounded-lg bg-white dark:bg-gray-900 text-sm transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700 border border-gray-200 dark:border-gray-800"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select 
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="in-progress">In Progress</option>
            <option value="planning">Planning</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
          </select>
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Project</span>
          </button>
        </div>
      </div>
      
      {/* Projects Grid */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {filteredProjects.map((project) => (
          <div 
            key={project.id} 
            className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <span className={`text-xs px-2.5 py-0.5 rounded-full ${getStatusColor(project.status)}`}>
                  {project.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </span>
                <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
              
              <h3 className="text-lg font-semibold mb-2">{project.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{project.description}</p>
              
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span>Progress</span>
                  <span>{project.progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      project.status === 'completed' 
                        ? 'bg-green-500 dark:bg-green-400' 
                        : project.status === 'on-hold'
                        ? 'bg-orange-500 dark:bg-orange-400'
                        : 'bg-blue-500 dark:bg-blue-400'
                    }`} 
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Due: {project.dueDate}</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {project.tasks.completed}/{project.tasks.total} tasks
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex -space-x-2">
                  {project.team.slice(0, 4).map((member) => (
                    <div 
                      key={member.id} 
                      className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium border-2 border-white dark:border-gray-900"
                      title={member.name}
                    >
                      {member.avatar}
                    </div>
                  ))}
                  {project.team.length > 4 && (
                    <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-medium border-2 border-white dark:border-gray-900">
                      +{project.team.length - 4}
                    </div>
                  )}
                </div>
                <button className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-200">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}